import React from 'react';
import { useTranslation } from 'react-i18next';
import PublicLayout from './PublicLayout';

const PrivacyPolicy = () => {
  const { t } = useTranslation();

  return (
    <PublicLayout>
      <div className="bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-sm p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">{t('Privacy Policy')}</h1>

          <div className="prose max-w-none">
            <p className="text-gray-600 mb-6">
              <strong>{t('Last updated:')}</strong> {new Date().toLocaleDateString()}
            </p>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">{t('1. Information We Collect')}</h2>
              <p className="text-gray-700 mb-4">
                {t('We collect information you provide directly to us, such as when you create an account, use our services, or contact us for support.')}
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li>{t('Personal identification information (name, email address, phone number)')}</li>
                <li>{t('Account credentials and preferences')}</li>
                <li>{t('Usage data and interaction with our services')}</li>
                <li>{t('Device information and technical data')}</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">{t('2. How We Use Your Information')}</h2>
              <p className="text-gray-700 mb-4">
                {t('We use the information we collect to:')}
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li>{t('Provide, maintain, and improve our services')}</li>
                <li>{t('Process transactions and send related information')}</li>
                <li>{t('Send technical notices and support messages')}</li>
                <li>{t('Respond to your comments and questions')}</li>
                <li>{t('Monitor and analyze trends and usage')}</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">{t('3. Information Sharing')}</h2>
              <p className="text-gray-700 mb-4">
                {t('We do not sell, trade, or otherwise transfer your personal information to third parties without your consent, except as described in this policy.')}
              </p>
              <p className="text-gray-700 mb-4">
                {t('We may share your information in the following circumstances:')}
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li>{t('With your explicit consent')}</li>
                <li>{t('To comply with legal obligations')}</li>
                <li>{t('To protect our rights and safety')}</li>
                <li>{t('With service providers who assist us in operating our services')}</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">{t('4. Data Security')}</h2>
              <p className="text-gray-700 mb-4">
                {t('We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction.')}
              </p>
              <p className="text-gray-700 mb-4">
                {t('However, no method of transmission over the internet or electronic storage is 100% secure, so we cannot guarantee absolute security.')}
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">{t('5. Data Retention')}</h2>
              <p className="text-gray-700 mb-4">
                {t('We retain your personal information for as long as necessary to provide our services, comply with legal obligations, resolve disputes, and enforce our agreements.')}
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">{t('6. Your Rights')}</h2>
              <p className="text-gray-700 mb-4">
                {t('Depending on your location, you may have the following rights regarding your personal information:')}
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li>{t('Access to your personal information')}</li>
                <li>{t('Correction of inaccurate information')}</li>
                <li>{t('Deletion of your personal information')}</li>
                <li>{t('Restriction of processing')}</li>
                <li>{t('Data portability')}</li>
                <li>{t('Objection to processing')}</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">{t('7. Cookies and Tracking')}</h2>
              <p className="text-gray-700 mb-4">
                {t('We use cookies and similar tracking technologies to collect and track information about your use of our services. You can control cookies through your browser settings.')}
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">{t('8. Third-Party Services')}</h2>
              <p className="text-gray-700 mb-4">
                {t('Our services may contain links to third-party websites or services. We are not responsible for the privacy practices of these third parties. We encourage you to read their privacy policies.')}
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">{t("9. Children's Privacy")}</h2>
              <p className="text-gray-700 mb-4">
                {t('Our services are not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13.')}
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">{t('10. Changes to This Policy')}</h2>
              <p className="text-gray-700 mb-4">
                {t('We may update this privacy policy from time to time. We will notify you of any changes by posting the new privacy policy on this page and updating the "Last updated" date.')}
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">{t('11. Contact Us')}</h2>
              <p className="text-gray-700 mb-4">
                {t('If you have any questions about this privacy policy or our privacy practices, please contact us at:')}
              </p>
              <div className="bg-gray-50 p-4 rounded-md">
                <p className="text-gray-700">
                  <strong>{t('Email:')}</strong> gpotier[at]globe-groupe[dot]com<br />
                  <strong>{t('Address:')}</strong> 11 Bd Jean Mermoz, 92200 Neuilly sur Seine<br />
                </p>
              </div>
            </section>
          </div>

          <div className="mt-8 pt-8 border-t border-gray-200">
            <button
              onClick={() => window.location.href = '/'}
              className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition-colors"
            >
              {t('Back to App')}
            </button>
          </div>
        </div>
      </div>
      </div>
    </PublicLayout>
  );
};

export default PrivacyPolicy;
